import React, {FunctionComponent, useMemo} from "react";
import {useSelector} from "react-redux";
import {css, Theme} from "@emotion/react";

import {mb} from "@pg-design/helpers-css";
import {getThemeBreakpoint} from "@pg-design/styles-strings";
import {Country} from "@pg-rp/common-types";

import {IRPStore} from "../../../app/rp_reducer";
import {TimeStamp} from "../../../atoms/TimeStamp";
import {TwoColumnGridHolder, TwoColumnGridMain} from "../../../atoms/TwoColumnGrid";
import {GTMEventApplicationSourceSection} from "../../../lead/types/GTMEvenApplicationSourceSection";
import {IParkingPlace} from "../../../property/interfaces/IParkingPlace";
import {OfferRankingModule} from "../../../ranking/components/OfferRankingModule";
import {getRankingItem} from "../../../ranking/utils/get_ranking_item";
import {IRegionIncludedLocation} from "../../../real_estate/types/IRegionIncludedLocation";
import {IRangeQuery} from "../../../request/IRangeQuery";
import {Currency} from "../../../types/Currency";
import {TagType} from "../../../types/TagType";
import {IValueLabel} from "../../../variables/types/IValueLabel";
import {VendorType} from "../../../vendor/helpers/VendorType";
import {OfferNavBar} from "../../atoms/OfferNavBar";
import {getOfferAmenities} from "../../helpers/get_offer_amenities";
import {OfferDisplayType} from "../../helpers/OfferDisplayType";
import {IRegionAncestor} from "../../list/actions/offer_list_selected_data_actions";
import {IOfferTag} from "../../types/IOfferTag";
import {OfferModalActiveTab} from "../../types/OfferModalActiveTab";
import {OfferType} from "../../types/OfferType";
import {INearestPlacesObject} from "../types/INearestPlace";
import {IOfferPoiDescription} from "../types/IOfferPoiDescription";
import {OfferDetailAmenities} from "./OfferDetailAmenities";
import {OfferDetailDescription} from "./OfferDetailDescription";
import {IStandardDescription, OfferDetailDetails} from "./OfferDetailDetails";
import {IRoomRange} from "./OfferDetailGeneratedDescription";
import {OfferDetailLocation} from "./OfferDetailLocation";
import {OfferVideo} from "./OfferVideo";

interface IProps {
    offer: {
        currency: Currency;
        address: string;
        buildings: number;
        create_date: string;
        configuration: {
            hide_publish_date: boolean;
            publish_date: string;
            show_generated_description: boolean;
            show_offer_plans: boolean;
            display_type: OfferDisplayType;
        };
        construction_date_range: IRangeQuery<string>;
        description: string | null;
        facilities: number[] | null;
        floors_above_ground_range: IRangeQuery<number> | null;
        floors_below_ground: number | null;
        geo_area: {
            coordinates: number[][][];
        };
        geo_point: {
            type: string;
            coordinates: [number, number];
        };
        id: number;
        investor: string | null;
        is_holiday_location: boolean;
        is_condohotel: boolean;
        location_description: string | null;
        logo: {
            o_log_200: string;
        } | null;
        name: string;
        natural_sites: number[] | null;
        parking_places: IParkingPlace[];
        poi_description: IOfferPoiDescription[];
        properties: number;
        region: {
            country: Country;
            full_name: string;
            name: string;
            id: number;
            included_location: IRegionIncludedLocation;
            name_declension_where: string;
            short_name_reverted: string;
            stats: {
                region_type_district: IRegionAncestor | null;
            } | null;
        };
        slug: string;
        standard_description: IStandardDescription;
        stats: {
            additional_areas: string[];
            all_flat_types: Record<string, number>;
            all_house_types: Record<string, number>;
            distance_from_region: {
                center: boolean;
                region: string;
                distance: number;
            };
            has_attic: boolean;
            has_entresol: boolean;
            last_update: string;
            nearest_places: INearestPlacesObject;
            ownership_forms: string[];
            properties_count_for_sale: number;
            properties_count_mdm: number;
            properties_with_roi_count: number;
            ranges_area_max: number;
            ranges_area_min: number;
            ranges_garages_count: number;
            ranges_height_max: number;
            ranges_height_min: number;
            ranges_price_m2_max: number;
            ranges_price_m2_min: number;
            ranges_price_min: number;
            ranges_rooms_max: number;
            ranges_rooms_min: number;
            rooms_ranges: IRoomRange[];
        } | null;
        storage: {
            quantity: number;
            description: string;
            price: number;
        };
        street_name: string;
        street_number: string | null;
        tags: IOfferTag[];
        type: OfferType;
        video: string | null;
        vendor: {
            awards_count: number;
            id: number;
            logo: {
                v_log_160x120: string;
            } | null;
            name: string;
            slug: string;
            type: VendorType;
            stats: {
                properties_count_for_sale: number;
                offers_count_for_sale: number;
                offers_count_sold: number;
            };
        };
    };
    refElems: {
        refGallery: React.RefObject<HTMLTableSectionElement | null>;
        refDescription: React.RefObject<HTMLTableSectionElement | null>;
        refLocation: React.RefObject<HTMLTableSectionElement | null>;
    };
    visibleElement: string | null;
    viewportHeight?: number;
    changeVisibility: (name: string) => void;
    changeModalTab: (tab: OfferModalActiveTab, source?: GTMEventApplicationSourceSection) => void;
    variables: {
        offer_natural_sites: IValueLabel[];
        offer_standard_description_type: IValueLabel[];
        offer_facilities: IValueLabel[];
        offer_funds_protection: IValueLabel[];
    } | null;
    hasDescription: boolean;
    shouldMapBeRendered?: boolean;
}

export const OfferDetailLimited: FunctionComponent<IProps> = React.memo((props) => {
    const {offer, refElems, changeModalTab, variables, hasDescription} = props;

    const publishDate = !offer.configuration.hide_publish_date && offer.configuration.publish_date ? offer.configuration.publish_date : null;
    const getNearbyParksTag = offer.tags.filter((tag: IOfferTag) => tag.id === TagType.GREEN_AREAS);
    const groupedOfferAmenities = useMemo(
        () => getOfferAmenities(offer.facilities, offer.natural_sites, getNearbyParksTag, offer.standard_description.types, variables),
        [offer.facilities, offer.natural_sites, offer.standard_description.types, variables]
    );
    const rankingItem = getRankingItem(offer.id);
    const transportPoiData = useSelector((state: IRPStore) => state.offerDetail.transportPoi);

    return (
        <>
            <OfferNavBar
                css={offerNavBarWrapperStyle}
                navBarContentStyle={navBarContentStyle}
                refElements={refElems}
                offer={offer}
                onButtonClick={changeModalTab}
                disableContact
            />

            <TwoColumnGridHolder>
                <TwoColumnGridMain>
                    {groupedOfferAmenities.length > 0 && (
                        <section css={offerDetailAmenitiesSection}>
                            <OfferDetailAmenities offerAmenities={groupedOfferAmenities} />
                        </section>
                    )}

                    <section css={detailsSectionStyle}>
                        <OfferDetailDetails offer={offer} variables={variables} showAmenities={groupedOfferAmenities.length === 0} />
                    </section>

                    {rankingItem && (
                        <section id="ranking" css={detailsSectionStyle}>
                            <OfferRankingModule rankingItem={rankingItem} />
                        </section>
                    )}

                    {offer.video && (
                        <section css={detailsSectionStyle}>
                            <OfferVideo url={offer.video} />
                        </section>
                    )}

                    {hasDescription && (
                        <section id="opis" ref={refElems.refDescription} css={scrollOffset}>
                            <OfferDetailDescription offer={offer} />
                        </section>
                    )}

                    {props.shouldMapBeRendered ? (
                        <section id="okolica" ref={refElems.refLocation} css={scrollOffset}>
                            {transportPoiData && transportPoiData.poi && (
                                <OfferDetailLocation
                                    offer={offer}
                                    geoAreaCoordinates={offer.geo_area.coordinates}
                                    geoPointCoordinates={offer.geo_point.coordinates}
                                    changeModalTab={changeModalTab}
                                    pois={transportPoiData.poi.pois}
                                />
                            )}
                        </section>
                    ) : null}

                    <section id="timestamp" css={timeStampSectionStyle}>
                        <TimeStamp lastUpdateDate={offer.stats?.last_update ?? ""} publishDate={publishDate} />
                    </section>
                </TwoColumnGridMain>
            </TwoColumnGridHolder>
        </>
    );
});

export const offerDescriptionSectionHeader = (theme: Theme) => css`
    padding: 3rem 0;
    font-size: 1.8rem;
    @media (min-width: ${theme.breakpoints.sm}) {
        font-size: 2.2rem;
        padding: 3rem 0;
    }
`;

const offerNavBarWrapperStyle = (theme: Theme) => css`
    margin: 0 0 0.3rem 0;
    display: none;

    @media (min-width: ${theme.breakpoints.md}) {
        display: block;
        margin: 0 0 2.4rem 0;
    }
`;

const navBarContentStyle = css`
    @media (min-width: ${getThemeBreakpoint().md}) and (max-width: 1310px) {
        margin-top: 60px; // other bars height
    }
`;

const timeStampSectionStyle = (theme: Theme) => css`
    ${mb(1)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(3)}
    }
`;

const offerDetailAmenitiesSection = (theme: Theme) => css`
    margin: 0 0 1.6rem 0;

    @media (min-width: ${theme.breakpoints.md}) {
        margin: 0 0 2.4rem 0;
    }
`;

const detailsSectionStyle = (theme: Theme) => css`
    margin: 0 0 1.6rem 0;

    @media (min-width: ${theme.breakpoints.md}) {
        margin: 0 0 2.4rem 0;
    }
`;

const scrollOffset = css`
    padding-top: 8rem;
    margin-top: -8rem;
`;
import React, {FunctionComponent, useMemo} from "react";
import {useSelector} from "react-redux";
import {css, Theme} from "@emotion/react";

import {mb} from "@pg-design/helpers-css";
import {getThemeBreakpoint} from "@pg-design/styles-strings";
import {Country} from "@pg-rp/common-types";

import {IRPStore} from "../../../app/rp_reducer";
import {TimeStamp} from "../../../atoms/TimeStamp";
import {TwoColumnGridHolder, TwoColumnGridMain} from "../../../atoms/TwoColumnGrid";
import {GTMEventApplicationSourceSection} from "../../../lead/types/GTMEvenApplicationSourceSection";
import {IParkingPlace} from "../../../property/interfaces/IParkingPlace";
import {OfferRankingModule} from "../../../ranking/components/OfferRankingModule";
import {getRankingItem} from "../../../ranking/utils/get_ranking_item";
import {IRegionIncludedLocation} from "../../../real_estate/types/IRegionIncludedLocation";
import {IRangeQuery} from "../../../request/IRangeQuery";
import {Currency} from "../../../types/Currency";
import {TagType} from "../../../types/TagType";
import {IValueLabel} from "../../../variables/types/IValueLabel";
import {VendorType} from "../../../vendor/helpers/VendorType";
import {OfferNavBar} from "../../atoms/OfferNavBar";
import {getOfferAmenities} from "../../helpers/get_offer_amenities";
import {OfferDisplayType} from "../../helpers/OfferDisplayType";
import {IRegionAncestor} from "../../list/actions/offer_list_selected_data_actions";
import {IOfferTag} from "../../types/IOfferTag";
import {OfferModalActiveTab} from "../../types/OfferModalActiveTab";
import {OfferType} from "../../types/OfferType";
import {INearestPlacesObject} from "../types/INearestPlace";
import {IOfferPoiDescription} from "../types/IOfferPoiDescription";
import {OfferDetailAmenities} from "./OfferDetailAmenities";
import {OfferDetailDescription} from "./OfferDetailDescription";
import {IStandardDescription, OfferDetailDetails} from "./OfferDetailDetails";
import {IRoomRange} from "./OfferDetailGeneratedDescription";
import {OfferDetailLocation} from "./OfferDetailLocation";
import {OfferVideo} from "./OfferVideo";

interface IProps {
    offer: {
        currency: Currency;
        address: string;
        buildings: number;
        create_date: string;
        configuration: {
            hide_publish_date: boolean;
            publish_date: string;
            show_generated_description: boolean;
            show_offer_plans: boolean;
            display_type: OfferDisplayType;
        };
        construction_date_range: IRangeQuery<string>;
        description: string | null;
        facilities: number[] | null;
        floors_above_ground_range: IRangeQuery<number> | null;
        floors_below_ground: number | null;
        geo_area: {
            coordinates: number[][][];
        };
        geo_point: {
            type: string;
            coordinates: [number, number];
        };
        id: number;
        investor: string | null;
        is_holiday_location: boolean;
        is_condohotel: boolean;
        location_description: string | null;
        logo: {
            o_log_200: string;
        } | null;
        name: string;
        natural_sites: number[] | null;
        parking_places: IParkingPlace[];
        poi_description: IOfferPoiDescription[];
        properties: number;
        region: {
            country: Country;
            full_name: string;
            name: string;
            id: number;
            included_location: IRegionIncludedLocation;
            name_declension_where: string;
            short_name_reverted: string;
            stats: {
                region_type_district: IRegionAncestor | null;
            } | null;
        };
        slug: string;
        standard_description: IStandardDescription;
        stats: {
            additional_areas: string[];
            all_flat_types: Record<string, number>;
            all_house_types: Record<string, number>;
            distance_from_region: {
                center: boolean;
                region: string;
                distance: number;
            };
            has_attic: boolean;
            has_entresol: boolean;
            last_update: string;
            nearest_places: INearestPlacesObject;
            ownership_forms: string[];
            properties_count_for_sale: number;
            properties_count_mdm: number;
            properties_with_roi_count: number;
            ranges_area_max: number;
            ranges_area_min: number;
            ranges_garages_count: number;
            ranges_height_max: number;
            ranges_height_min: number;
            ranges_price_m2_max: number;
            ranges_price_m2_min: number;
            ranges_price_min: number;
            ranges_rooms_max: number;
            ranges_rooms_min: number;
            rooms_ranges: IRoomRange[];
        } | null;
        storage: {
            quantity: number;
            description: string;
            price: number;
        };
        street_name: string;
        street_number: string | null;
        tags: IOfferTag[];
        type: OfferType;
        video: string | null;
        vendor: {
            awards_count: number;
            id: number;
            logo: {
                v_log_160x120: string;
            } | null;
            name: string;
            slug: string;
            type: VendorType;
            stats: {
                properties_count_for_sale: number;
                offers_count_for_sale: number;
                offers_count_sold: number;
            };
        };
    };
    refElems: {
        refGallery: React.RefObject<HTMLTableSectionElement | null>;
        refDescription: React.RefObject<HTMLTableSectionElement | null>;
        refLocation: React.RefObject<HTMLTableSectionElement | null>;
    };
    visibleElement: string | null;
    viewportHeight?: number;
    changeVisibility: (name: string) => void;
    changeModalTab: (tab: OfferModalActiveTab, source?: GTMEventApplicationSourceSection) => void;
    variables: {
        offer_natural_sites: IValueLabel[];
        offer_standard_description_type: IValueLabel[];
        offer_facilities: IValueLabel[];
        offer_funds_protection: IValueLabel[];
    } | null;
    hasDescription: boolean;
    shouldMapBeRendered?: boolean;
}

export const OfferDetailLimited: FunctionComponent<IProps> = React.memo((props) => {
    const {offer, refElems, changeModalTab, variables, hasDescription} = props;

    const publishDate = !offer.configuration.hide_publish_date && offer.configuration.publish_date ? offer.configuration.publish_date : null;
    const getNearbyParksTag = offer.tags.filter((tag: IOfferTag) => tag.id === TagType.GREEN_AREAS);
    const groupedOfferAmenities = useMemo(
        () => getOfferAmenities(offer.facilities, offer.natural_sites, getNearbyParksTag, offer.standard_description.types, variables),
        [offer.facilities, offer.natural_sites, offer.standard_description.types, variables]
    );
    const rankingItem = getRankingItem(offer.id);
    const transportPoiData = useSelector((state: IRPStore) => state.offerDetail.transportPoi);

    return (
        <>
            <OfferNavBar
                css={offerNavBarWrapperStyle}
                navBarContentStyle={navBarContentStyle}
                refElements={refElems}
                offer={offer}
                onButtonClick={changeModalTab}
                disableContact
            />

            <TwoColumnGridHolder>
                <TwoColumnGridMain>
                    {groupedOfferAmenities.length > 0 && (
                        <section css={offerDetailAmenitiesSection}>
                            <OfferDetailAmenities offerAmenities={groupedOfferAmenities} />
                        </section>
                    )}

                    <section css={detailsSectionStyle}>
                        <OfferDetailDetails offer={offer} variables={variables} showAmenities={groupedOfferAmenities.length === 0} />
                    </section>

                    {rankingItem && (
                        <section id="ranking" css={detailsSectionStyle}>
                            <OfferRankingModule rankingItem={rankingItem} />
                        </section>
                    )}

                    {offer.video && (
                        <section css={detailsSectionStyle}>
                            <OfferVideo url={offer.video} />
                        </section>
                    )}

                    {hasDescription && (
                        <section id="opis" ref={refElems.refDescription} css={scrollOffset}>
                            <OfferDetailDescription offer={offer} />
                        </section>
                    )}

                    {props.shouldMapBeRendered ? (
                        <section id="okolica" ref={refElems.refLocation} css={scrollOffset}>
                            {transportPoiData && transportPoiData.poi && (
                                <OfferDetailLocation
                                    offer={offer}
                                    geoAreaCoordinates={offer.geo_area.coordinates}
                                    geoPointCoordinates={offer.geo_point.coordinates}
                                    changeModalTab={changeModalTab}
                                    pois={transportPoiData.poi.pois}
                                />
                            )}
                        </section>
                    ) : null}

                    <section id="timestamp" css={timeStampSectionStyle}>
                        <TimeStamp lastUpdateDate={offer.stats?.last_update ?? ""} publishDate={publishDate} />
                    </section>
                </TwoColumnGridMain>
            </TwoColumnGridHolder>
        </>
    );
});

export const offerDescriptionSectionHeader = (theme: Theme) => css`
    padding: 3rem 0;
    font-size: 1.8rem;
    @media (min-width: ${theme.breakpoints.sm}) {
        font-size: 2.2rem;
        padding: 3rem 0;
    }
`;

const offerNavBarWrapperStyle = (theme: Theme) => css`
    margin: 0 0 0.3rem 0;
    display: none;

    @media (min-width: ${theme.breakpoints.md}) {
        display: block;
        margin: 0 0 2.4rem 0;
    }
`;

const navBarContentStyle = css`
    @media (min-width: ${getThemeBreakpoint().md}) and (max-width: 1310px) {
        margin-top: 60px; // other bars height
    }
`;

const timeStampSectionStyle = (theme: Theme) => css`
    ${mb(1)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(3)}
    }
`;

const offerDetailAmenitiesSection = (theme: Theme) => css`
    margin: 0 0 1.6rem 0;

    @media (min-width: ${theme.breakpoints.md}) {
        margin: 0 0 2.4rem 0;
    }
`;

const detailsSectionStyle = (theme: Theme) => css`
    margin: 0 0 1.6rem 0;

    @media (min-width: ${theme.breakpoints.md}) {
        margin: 0 0 2.4rem 0;
    }
`;

const scrollOffset = css`
    padding-top: 8rem;
    margin-top: -8rem;
`;
